import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { Checkbox, MaskedInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";

import type { LienFormValues } from "../../LienTypes";

type CPNSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const CPNSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
  constants,
}: CPNSectionProps) => {
  const cpnReceived = getValues("cpn_received") ?? false;

  const appealResultOptions = constants?.CRCAppealResultChoices || [];

  return (
    <>
      <Checkbox
        name="cpn_received"
        label="CPN Received"
        checked={getValues("cpn_received")}
        required={false}
        onChange={async (e) => {
          setValue("cpn_received", e.target.checked);
          if (!e.target.checked) {
            setValue("cpn_amount", null);
            setValue("cpn_due_date", null);
            setValue("cpn_dispute_date", null);
            setValue("cpn_date_issued", null);
            setValue("cpn_result", null);
            setValue("cpn_appeal_result_dollar_amount", null);
            setValue("cpn_savings", null);
            setValue("cpn_total_disputed", null);
            await handleFieldChange(null, {
              cpn_received: false,
              cpn_amount: null,
              cpn_due_date: null,
              cpn_dispute_date: null,
              cpn_date_issued: null,
              cpn_result: null,
              cpn_appeal_result_dollar_amount: null,
              cpn_savings: null,
              cpn_total_disputed: null,
            });
          } else {
            await handleFieldChange("cpn_received", e.target.checked);
          }
        }}
      />

      {cpnReceived && (
        <>
          <ControlledDateInput
            control={control}
            name="cpn_date_issued"
            label="CPN Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("cpn_date_issued", value)}
          />
          <ControlledInput
            control={control}
            name="cpn_amount"
            label="CPN Amount"
            errors={{}}
            onSave={(value) => handleFieldChange("cpn_amount", value)}
            componentType={MaskedInput}
            mask="dollar"
          />
          <ControlledInput
            control={control}
            name="cpn_total_disputed"
            label="CPN Total Disputed"
            errors={{}}
            onSave={(value) => handleFieldChange("cpn_total_disputed", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />
          <ControlledInput
            control={control}
            name="cpn_amount_due"
            label="CPN Amount Due"
            errors={{}}
            onSave={(value) => handleFieldChange("cpn_amount_due", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />
          <ControlledDateInput
            control={control}
            name="cpn_due_date"
            label="CPN Due Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("cpn_due_date", value)}
          />
          <ControlledDateInput
            control={control}
            name="cpn_dispute_date"
            label="CPN Dispute Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("cpn_dispute_date", value)}
          />

          <ControlledSelect
            control={control}
            name="cpn_result"
            label="CPN Result"
            errors={{}}
            options={appealResultOptions}
            onSave={(value) =>
              handleFieldChange("cpn_result", value?.value || null)
            }
          />

          <ControlledInput
            control={control}
            name="cpn_appeal_result_dollar_amount"
            label="CPN Appeal Result Dollar Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("cpn_appeal_result_dollar_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="cpn_savings"
            label="CPN Savings"
            errors={{}}
            onSave={(value) => handleFieldChange("cpn_savings", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />
        </>
      )}
    </>
  );
};

export default CPNSection;
